// Provedores globais (Parte 14.2 app/): i18n, tema, TanStack Query, cliente da API, autenticação (OIDC/fake) e roteador.
import { QueryClientProvider } from '@tanstack/react-query';
import type { i18n as I18nInstance } from 'i18next';
import { type ReactNode, useState } from 'react';
import { I18nextProvider } from 'react-i18next';
import { RouterProvider } from 'react-router';

import { ApiClientProvider } from '../lib/api/ApiClientProvider';
import { AppEnvContext } from '../lib/appEnvContext';
import { AuthProvider } from '../lib/auth/AuthProvider';
import { createAuthBackend } from '../lib/auth/createAuthBackend';
import type { AuthBackend } from '../lib/auth/types';
import { type AppEnv, env as buildEnv } from '../lib/env';
import { createQueryClient } from '../lib/query/queryClient';
import { ThemeProvider } from '../lib/theme/ThemeProvider';
import { createAppRouter } from './router';

export interface AppProvidersProps {
  readonly i18n: I18nInstance;
  /** Configuração efetiva (`.env` + `/config.json`, Parte 14.8); padrão: só o `.env`. */
  readonly env?: AppEnv;
}

/** Composição de provedores sem o roteador (reutilizada pelos testes com roteador em memória). */
export interface CoreProvidersProps {
  readonly i18n: I18nInstance;
  readonly backend: AuthBackend;
  readonly queryClient: ReturnType<typeof createQueryClient>;
  readonly env: AppEnv;
  readonly children: ReactNode;
}

export function CoreProviders({ i18n, backend, queryClient, env, children }: CoreProvidersProps) {
  return (
    <AppEnvContext value={env}>
      <I18nextProvider i18n={i18n}>
        <ThemeProvider>
          <QueryClientProvider client={queryClient}>
            <ApiClientProvider baseUrl={env.apiBaseUrl} backend={backend}>
              <AuthProvider backend={backend}>{children}</AuthProvider>
            </ApiClientProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </I18nextProvider>
    </AppEnvContext>
  );
}

export function AppProviders({ i18n, env = buildEnv }: AppProvidersProps) {
  const [queryClient] = useState(createQueryClient);
  const [backend] = useState(() => createAuthBackend(env, window.location.origin));
  const [router] = useState(createAppRouter);
  return (
    <CoreProviders i18n={i18n} backend={backend} queryClient={queryClient} env={env}>
      <RouterProvider router={router} />
    </CoreProviders>
  );
}
